import {observable} from "mobx";
import {O} from "web-vcore";
import {Rect, Vec2, Vec3} from "../../../Utils/ThreeJS/VectorStructs.js";
import {MapSettings} from "./@MapState/MapSettings.js";
import {ignore} from "mobx-sync";

export class MapState {
	//@O @ignore initDone = false; // init must re-happen each time map is loaded, so that default-values for props (which might not have existed at time map-state was first created) are re-applied

	@O @ignore accessor camPos_0ms = new Vec3(0, 0, 100);
	//@O @ignore camPos_100ms = new Vec3(0, 0, 100);
	@O accessor camPos_500ms = new Vec3(0, 0, 100); // throttled to 500ms; see MapSurface.tsx

	//@O camRot_0ms = new Vec3(90, 0, 0); // cam-rot for looking straight down

	@O @ignore accessor camZoom_0ms = 1;
	@O accessor camZoom_500ms = 1; // throttled to 500ms; see MapSurface.tsx

	@O @ignore accessor mousePos_0ms: Vec2|n;
	@O @ignore accessor mousePos_500ms: Vec2|n; // throttled to 500ms; see MapControls.ts
	@O @ignore accessor mousePos_world_0ms: Vec3|n;
	@O @ignore accessor mousePos_world_500ms: Vec3|n; // throttled to 500ms; see MapControls.ts

	@O accessor cursorPos = new Vec3(0, 0, 0);
	@O accessor selectedObjects = [] as string[];
	@O accessor activeObject: string|n;
	/** Reference object; unaffected by regular object-selection changes. When to utilize this:
	 * 1) For objects/roles that should be more "stable" than the user's frequent selection changes. (eg. which object should be the parent of newly-created objects)
	 * 2) For info labels or overlays, that want to display info about the selection (relative to some object), while still in the process of building up the selection. */
	@O accessor referenceObject: string|n;
	@O accessor expandedObjects = {} as Record<string, true>; // object-ids of objects that are expanded in the outliner (missing = collapsed)

	// menu bar (here for now, since this ui setup probably temp)
	@O accessor snap_pos_enabled = true;
	@O accessor snap_pos_step = 0.1;
	/*@O snap_rot_enabled = true;
	@O accessor snap_rot_step = 15;*/

	@O accessor topRightPanel = "outliner" as "outliner" | "settings" | "stats";
	@O accessor stats_objRowsToShow = 20;
	/** Fraction (0-1) of the map-sidebar's height taken by the top panel (RightTopPanel); the rest goes to the bottom panel (PropertiesUI). Persisted. */
	@O accessor sidebarTopPanelPercent = .6;
	@O accessor outliner = new OutlinerPanelState();
	@O accessor settings = new MapSettings();
	@O accessor propertiesPanel = new PropertiesPanelState();
	/** Buildings Set1 (§6.3): the active floor shown in the 2D plan view (defaults to 1). A small
	 *  floor-stepper in the Building panel changes it; without it a tower renders all floors
	 *  stacked in plan view. */
	@O accessor buildings = new BuildingsPanelState();
}

export class BuildingsPanelState {
	/** The 2D plan view's active floor number (1-based; -1, -2 are basements). */
	@O accessor activeFloor = 1;
}

export class OutlinerPanelState {
	@O accessor filter_text = "";
	@O accessor filter_onlyVisibleOrSelected = false;
	@O accessor filter_matchPassThrough = false;
}

export class PropertiesPanelState {
	@O accessor tab = PropertiesTab.general;

	// general
	@O accessor place_random_rect = new Rect(0, 0, 100, 100, true);
	@O accessor place_random_minDist = 10;
	@O accessor place_random_attempts = 100;

	// paths
	@O accessor path_tab = PathTab.general;
	/** Set12 P7 — the path editor's selected path index (per-object; the material-overview list
	 *  in the terrain-materials modifier can jump to a path by setting this + `path_tab`). */
	@O accessor paths_selectedIndex = 0;

	// modifiers
	@O accessor terrainDefinition_tab = TerrainDefinitionTab.ambient;
	@O accessor terrainDefinition_selectedColorGradient_index = 0;
	@O accessor terrainMaterials_tab = TerrainMaterialsTab.recipes;
	@O accessor terrainSceneries_tab = TerrainSceneriesTab.kinds;
	@O accessor terrainSceneries_collapsedGroups = observable.map<string, boolean>();
}

export enum PropertiesTab {
	general = "general",
	visuals = "visuals",
	meshes = "meshes",
	paths = "paths",
	modifiers = "modifiers",
	journal = "journal",
}

export enum PathTab {
	general = "general",
	points = "points",
	transforms = "transforms",
	fill = "fill",
	terrain = "terrain",
	/** Set12 P7 — the path's material-assignment feature (`ObjPath.materialFeature`): which
	 *  recipe this path paints + how the geometry becomes a region (fill/stroke/fillAndStroke)
	 *  + the blend fields.  Mirrors the terrain-feature tab. */
	material = "material",
	/** Set13 P6 — the path's scenery-placement feature (`ObjPath.sceneryFeature`): which
	 *  scenery kind this path places + how the geometry becomes a region (fill/stroke/
	 *  fillAndStroke) + the density knobs.  Mirrors the material tab; scenery is ORTHOGONAL to
	 *  material (Set13 §4.10). */
	scenery = "scenery",
}

export enum TerrainDefinitionTab {
	ambient = "ambient",
	solver = "solver",
	shading = "shading",
}

/** Set13 P6 — the terrain-sceneries modifier's UI tabs: user-authored kind defs + the global
 *  scenery budget/quality knobs (§3.7). */
export enum TerrainSceneriesTab {
	/** User-authored scenery kinds (the kind editor — the Set13 §5 P6 spec: two plain sliders
	 *  per param, value + swing, with a read-only `[min … max]` readout + a variant preview). */
	kinds = "kinds",
	/** Global budget/quality knobs (`SceneryBudgetConfig`, §3.7). */
	budget = "budget",
}

/** Set9 Phase 7 — the terrain-materials modifier's UI tabs.  Set12 removed the `regions` tab
 *  (material regions moved ONTO the paths — `ObjPath.materialFeature`; the path-side editor is
 *  the path's "material" tab, P7).  Set12 P7 restores it as a READ-ONLY OVERVIEW: every path
 *  region on the map + its nesting depth, with links back to the owning path. */
export enum TerrainMaterialsTab {
	recipes = "recipes",
	/** Set12 P7 — read-only overview of the map's path-driven material regions (the §4.6
	 *  "read-only list in TerrainMaterials_ModUI that links to each path" mitigation for
	 *  authoring living on the paths). */
	regions = "regions",
	/** Map-wide fallbacks: the base recipe + global blend defaults (`baseRecipeID`,
	 *  `blendDefaults` on `Mod_TerrainMaterials`). */
	fallbacks = "fallbacks",
}